import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Switch,
  Popconfirm,
  Typography,
  Descriptions
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  AndroidOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  StopOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import PageLayout from './common/PageLayout';
import { useSimpleStore } from '../stores/simpleStore';
import { config } from '../config';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ApkVersion {
  id: number;
  version_code: number;
  version_name: string;
  file_name: string;
  file_path: string;
  file_size: number;
  checksum: string;
  company_id: number | null;
  company_name: string | null;
  status: 'draft' | 'testing' | 'stable' | 'deprecated';
  is_mandatory: number;
  release_notes: string;
  uploader_name: string;
  created_at: string;
}

export default function ApkManagement() {
  const [apkList, setApkList] = useState<ApkVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedApk, setSelectedApk] = useState<ApkVersion | null>(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const selectedCompanyId = useSimpleStore((state) => state.selectedCompanyId);
  const selectedCompanyName = useSimpleStore((state) => state.selectedCompanyName);

  // APK 목록 로드
  const loadApkList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.apiBaseUrl}/api/apk/list?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setApkList(data.data.items);
      } else {
        message.error(data.error || 'APK 목록 로드 실패');
      }
    } catch (error) {
      console.error('APK 목록 로드 에러:', error);
      message.error('APK 목록 로드 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApkList();
  }, []);

  // APK 업로드
  const handleUpload = async (values: any) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', values.file[0].originFileObj);
      formData.append('version_code', values.version_code);
      formData.append('version_name', values.version_name);
      formData.append('company_id', values.company_id === 'all' ? '' : values.company_id);
      formData.append('is_mandatory', values.is_mandatory ? 'true' : 'false');
      formData.append('release_notes', values.release_notes || '');

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.apiBaseUrl}/api/apk/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        const extracted = data.data?.extractedFromApk;
        if (extracted && extracted.versionName) {
          message.success(`APK 업로드 성공 (자동 추출: v${extracted.versionName}, 코드: ${extracted.versionCode})`);
        } else {
          message.success('APK 업로드 성공');
        }
        setUploadModalVisible(false);
        form.resetFields();
        loadApkList();
      } else {
        message.error(data.error || 'APK 업로드 실패');
      }
    } catch (error) {
      console.error('APK 업로드 에러:', error);
      message.error('APK 업로드 중 오류가 발생했습니다');
    } finally {
      setUploading(false);
    }
  };

  // APK 상태 변경
  const handleStatusChange = async (id: number, status: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.apiBaseUrl}/api/apk/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      const data = await response.json();
      if (data.success) {
        message.success('상태 변경 완료');
        loadApkList();
      } else {
        message.error(data.error || '상태 변경 실패');
      }
    } catch (error) {
      console.error('상태 변경 에러:', error);
      message.error('상태 변경 중 오류가 발생했습니다');
    }
  };

  // APK 다운로드
  const handleDownload = (apk: ApkVersion) => {
    const token = localStorage.getItem('adminToken');
    window.open(`${config.apiBaseUrl}/api/apk/download/${apk.id}?token=${token}`, '_blank');
  };

  // APK 삭제
  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.apiBaseUrl}/api/apk/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        message.success('APK 삭제 완료');
        loadApkList();
      } else {
        message.error(data.error || 'APK 삭제 실패');
      }
    } catch (error) {
      console.error('APK 삭제 에러:', error);
      message.error('APK 삭제 중 오류가 발생했습니다');
    }
  };

  // 상태 태그 렌더링
  const renderStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      draft: { color: 'default', icon: <ClockCircleOutlined />, text: '임시저장' },
      testing: { color: 'processing', icon: <ExperimentOutlined />, text: '테스트 중' },
      stable: { color: 'success', icon: <CheckCircleOutlined />, text: '배포 중' },
      deprecated: { color: 'error', icon: <StopOutlined />, text: '사용중지' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title: '버전',
      key: 'version',
      render: (record: ApkVersion) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>{record.version_name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>Code: {record.version_code}</Text>
        </div>
      )
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status)
    },
    {
      title: '배포 대상',
      key: 'target',
      render: (record: ApkVersion) => (
        <div>
          {record.company_id === null ? (
            <Tag color="blue">전체 공통</Tag>
          ) : (
            <Tag>{record.company_name}</Tag>
          )}
          {record.is_mandatory === 1 && <Tag color="red">필수</Tag>}
        </div>
      )
    },
    {
      title: '파일 정보',
      key: 'file',
      render: (record: ApkVersion) => (
        <div>
          <div>{record.file_name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{formatFileSize(record.file_size)}</Text>
        </div>
      )
    },
    {
      title: '업로드 정보',
      key: 'upload',
      render: (record: ApkVersion) => (
        <div>
          <div>{record.uploader_name || '알 수 없음'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {new Date(record.created_at).toLocaleString('ko-KR')}
          </Text>
        </div>
      )
    },
    {
      title: '작업',
      key: 'action',
      render: (record: ApkVersion) => (
        <Space>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
          >
            다운로드
          </Button>
          {record.status === 'draft' && (
            <Button
              size="small"
              type="primary"
              onClick={() => handleStatusChange(record.id, 'stable')}
            >
              배포
            </Button>
          )}
          {record.status === 'stable' && (
            <Button
              size="small"
              danger
              onClick={() => handleStatusChange(record.id, 'deprecated')}
            >
              중단
            </Button>
          )}
          {record.status !== 'stable' && (
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              onConfirm={() => handleDelete(record.id)}
              okText="삭제"
              cancelText="취소"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                삭제
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            onClick={() => {
              setSelectedApk(record);
              setDetailModalVisible(true);
            }}
          >
            상세
          </Button>
        </Space>
      )
    }
  ];

  // 통계 계산
  const stats = {
    total: apkList.length,
    stable: apkList.filter(a => a.status === 'stable').length,
    testing: apkList.filter(a => a.status === 'testing').length,
    totalSize: apkList.reduce((sum, a) => sum + a.file_size, 0)
  };

  // 업로드 설정
  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      const isApk = file.name.endsWith('.apk');
      if (!isApk) {
        message.error('APK 파일만 업로드 가능합니다');
      }
      const isLt200M = file.size / 1024 / 1024 < 200;
      if (!isLt200M) {
        message.error('APK 파일은 200MB 이하만 가능합니다');
      }
      return isApk && isLt200M;
    },
    maxCount: 1,
    customRequest: ({ onSuccess }) => {
      // 파일 선택만 처리, 실제 업로드는 Form.onFinish에서 수행
      setTimeout(() => {
        onSuccess?.('ok');
      }, 0);
    }
  };

  return (
    <PageLayout title="📱 APK 버전 관리">
      {/* 통계 카드 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="전체 버전"
              value={stats.total}
              prefix={<AndroidOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="배포 중"
              value={stats.stable}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="테스트 중"
              value={stats.testing}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ExperimentOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="총 용량"
              value={formatFileSize(stats.totalSize)}
              suffix=""
            />
          </Card>
        </Col>
      </Row>

      {/* APK 목록 */}
      <Card
        title="APK 버전 목록"
        extra={
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            APK 업로드
          </Button>
        }
      >
        <Table
          dataSource={apkList}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `전체 ${total}개`
          }}
        />
      </Card>

      {/* 업로드 모달 */}
      <Modal
        title="APK 업로드"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            name="file"
            label="APK 파일"
            rules={[{ required: true, message: 'APK 파일을 선택하세요' }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload {...uploadProps} accept=".apk">
              <Button icon={<UploadOutlined />}>파일 선택 (.apk)</Button>
            </Upload>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="version_code"
                label="버전 코드"
                tooltip="APK 파일에서 자동으로 추출됩니다. 수동 입력 시 APK 값을 덮어씁니다."
              >
                <Input
                  type="number"
                  placeholder="APK에서 자동 추출 (선택사항)"
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="version_name"
                label="버전 이름"
                tooltip="APK 파일에서 자동으로 추출됩니다. 수동 입력 시 APK 값을 덮어씁니다."
              >
                <Input placeholder="APK에서 자동 추출 (선택사항)" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
            <Text type="secondary">
              💡 <strong>자동 버전 추출:</strong> APK 파일을 선택하면 버전 정보가 자동으로 추출됩니다.
              버전 필드를 비워두면 APK에서 추출한 값을 사용하고, 수동 입력하면 덮어씁니다.
            </Text>
          </div>

          <Form.Item
            name="company_id"
            label="배포 대상"
            initialValue="all"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="all">전체 공통 (모든 단말기)</Select.Option>
              <Select.Option value={selectedCompanyId || ''} disabled={!selectedCompanyId}>
                {selectedCompanyName || '운수사 선택 필요'}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_mandatory"
            label="필수 업데이트"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="필수" unCheckedChildren="선택" />
          </Form.Item>

          <Form.Item
            name="release_notes"
            label="릴리즈 노트"
          >
            <TextArea
              rows={4}
              placeholder="업데이트 내용을 입력하세요"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                업로드
              </Button>
              <Button onClick={() => {
                setUploadModalVisible(false);
                form.resetFields();
              }}>
                취소
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 상세 정보 모달 */}
      <Modal
        title="APK 상세 정보"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            닫기
          </Button>
        ]}
        width={700}
      >
        {selectedApk && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="버전 이름" span={1}>
              {selectedApk.version_name}
            </Descriptions.Item>
            <Descriptions.Item label="버전 코드" span={1}>
              {selectedApk.version_code}
            </Descriptions.Item>
            <Descriptions.Item label="상태" span={1}>
              {renderStatusTag(selectedApk.status)}
            </Descriptions.Item>
            <Descriptions.Item label="필수 업데이트" span={1}>
              {selectedApk.is_mandatory === 1 ? <Tag color="red">필수</Tag> : <Tag>선택</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="배포 대상" span={2}>
              {selectedApk.company_id === null ? '전체 공통' : selectedApk.company_name}
            </Descriptions.Item>
            <Descriptions.Item label="파일명" span={2}>
              {selectedApk.file_name}
            </Descriptions.Item>
            <Descriptions.Item label="파일 크기" span={1}>
              {formatFileSize(selectedApk.file_size)}
            </Descriptions.Item>
            <Descriptions.Item label="체크섬" span={1}>
              <Text code style={{ fontSize: 11 }}>
                {selectedApk.checksum.substring(0, 16)}...
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="업로더" span={1}>
              {selectedApk.uploader_name}
            </Descriptions.Item>
            <Descriptions.Item label="업로드 일시" span={1}>
              {new Date(selectedApk.created_at).toLocaleString('ko-KR')}
            </Descriptions.Item>
            <Descriptions.Item label="릴리즈 노트" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {selectedApk.release_notes || '없음'}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </PageLayout>
  );
}
